import { isObjectEmpty } from "utils/helpers/general"
import { IFinalEvent } from "types/INewEvent"
import { ISelectedTicket } from "types/improved-booking"
import dayjs from "dayjs"
import { ItimeSlot } from "types/ticket"

/**
 * Helper functions to determine which booking steps should be displayed
 * based on the current booking state
 */

/**
 * Determines if the select day step should be shown
 * @param availableTimeslots The available time slots for the event
 * @returns Whether the select day step should be shown
 */
export const shouldShowSelectDayStep = (
    availableTimeslots: ItimeSlot[]
): boolean => {
    // Only show day selection if there are multiple options
    return availableTimeslots.length > 1
}

/**
 * Determines if the select time step should be shown
 * @param selectedDateKey The YYYY-MM-DD key of the selected day (or null if none selected)
 * @param availableTimeslots The available time slots for the event
 * @returns Whether the select time step should be shown
 */
export const shouldShowSelectTimeStep = (
    selectedDateKey: string | null,
    availableTimeslots: ItimeSlot[]
): boolean => {
    if (!selectedDateKey) return false

    const daySlots = availableTimeslots.filter(
        (slot) =>
            dayjs(slot.start_slot).format("YYYY-MM-DD") === selectedDateKey
    )

    return daySlots.length > 1
}

/**
 * Determines if the select seat step should be shown
 * @param event The event data
 * @param selectedTickets The selected tickets
 * @returns Whether the select seat step should be shown
 */
export const shouldShowSelectSeatStep = (
    event: IFinalEvent,
    selectedTickets: ISelectedTicket[]
): boolean => {
    // For EID party events, always show seat selection
    if (
        [
            process.env.NEXT_PUBLIC_EID_PARTY_1,
            process.env.NEXT_PUBLIC_EID_PARTY_2,
            process.env.NEXT_PUBLIC_EID_PARTY_3,
        ].includes(event?._id)
    ) {
        return true
    }

    const hasMustSelectSeatTicket = selectedTickets.some(
        (ticket: ISelectedTicket) => ticket.must_select_seat === true
    )

    const eventDisplaySeatMap = (event as any)?.display_seat_map === true

    return hasMustSelectSeatTicket || eventDisplaySeatMap
}

/**
 * Determines if the select tickets step should be shown
 * @param event The event data
 * @returns Whether the select tickets step should be shown
 */
export const shouldShowSelectTicketsStep = (event?: IFinalEvent): boolean => {
    // For EID party events, skip ticket selection
    if (
        event &&
        [
            process.env.NEXT_PUBLIC_EID_PARTY_1,
            process.env.NEXT_PUBLIC_EID_PARTY_2,
            process.env.NEXT_PUBLIC_EID_PARTY_3,
        ].includes(event._id)
    ) {
        return false
    }

    // For all other events, this step is always shown
    return true
}

/**
 * Determines if the accreditation step should be shown
 * @param _event The event data (unused but kept for compatibility)
 * @returns Whether the accreditation step should be shown
 */
export const shouldShowAccreditationStep = (_event?: IFinalEvent): boolean => {
    // For now, this step is always hidden
    // Would be shown if event.accreditation.questions has items
    if (_event?.accreditation && _event.accreditation.questions.length > 0) {
        return true
    }
    return false
}

/**
 * Determines if the add-ons step should be shown
 * @param event The event data
 * @returns Whether the add-ons step should be shown
 */
export const shouldShowAddOnsStep = (event?: IFinalEvent): boolean => {
    // Show add-ons step if the event has extras configured
    return !!(event?.extras && event.extras.length > 0)
}

/**
 * Determines if the checkout step should be shown
 * @param _event The event data (unused but kept for compatibility)
 * @returns Whether the checkout step should be shown
 */
export const shouldShowCheckoutStep = (_event?: IFinalEvent): boolean => {
    // This step is always shown
    // Future implementation might check for required seats
    return true
}
